'use client';

import { MouseEvent, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { ProductsContext } from 'context-providers/products-provider';
import { mqLargeMin } from 'styled/tools/media-queries';
import { useInView } from 'react-intersection-observer';
import useProgressiveImg from 'hooks/use-progressive-image';
import MutableNextLink from 'components/reusable/mutable-next-line';
import { trackButtonEvent } from 'lib/analytics';
import { CartContext } from 'context-providers/cart-provider';
import type { Product } from 'entities/product';
import type { BundleModule } from 'lib/pdp-modules/types';

const BundleWrapper = styled.div`
  margin: 0.5rem;
  border-radius: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  background-color: var(--fill-brand);
  overflow: hidden;
  transition:
    margin 0.5s,
    border-radius 0.5s;

  &.visible {
    margin: 0;
    border-radius: 0;
  }

  @media ${mqLargeMin} {
    margin: 2rem;

    &.visible {
      margin: 0;
    }
  }

  * {
    color: white;
  }
`;

const BundleContent = styled.div`
  padding: 2rem 0;
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  align-items: flex-start;

  @media ${mqLargeMin} {
    padding: 8.125rem 3.5rem 3.5rem;
    flex-direction: row;
  }
`;

const BundleTextWrapper = styled.div`
  max-width: 32.5rem;
  padding: 0 1.5rem;

  @media ${mqLargeMin} {
    padding: 0;
    margin-top: 2rem;
  }

  p {
    margin-bottom: 0.75rem;
  }
`;

const BundleContentTitle = styled.h2`
  margin-bottom: 1.5rem;
  font-size: 32px;
  font-weight: 350;
  letter-spacing: -0.64px;
  text-wrap: auto;
`;

const AddBundleToCart = styled(MutableNextLink)`
  appearance: none;
  margin-bottom: 7.25rem;
  margin-top: 1.5rem;
  border: 0;
  border-radius: 6.25rem;
  padding: 1rem 1.5rem;
  height: 3rem;
  display: inline-flex;
  align-items: center;
  background-color: var(--fill-primary);
  color: var(--text-primary);
  cursor: pointer;
  text-decoration: none;

  @media ${mqLargeMin} {
    margin-bottom: 0;
  }
`;

const BundleImageWrapper = styled.div`
  position: relative;
`;

const BundleLabel = styled.span`
  position: absolute;
  top: -4rem;
  left: 32%;
  transform: translateX(-50%);
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 110%;
  letter-spacing: 0.24px;
  text-transform: uppercase;
  opacity: 0;
  transition:
    opacity 0.5s 0.5s,
    top 0.5s 0.5s;

  .visible & {
    top: -3rem;
    opacity: 1;
  }

  &::before {
    content: '';
    position: absolute;
    top: 150%;
    left: 50%;
    height: 2rem;
    border-left: 1px dotted var(--outline-neutral);

    @media ${mqLargeMin} {
      height: 2.5rem;
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: calc(150% + 2rem);
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 100%;
    width: 5px;
    height: 5px;
    background: var(--fill-primary);

    @media ${mqLargeMin} {
      top: calc(150% + 2.5rem);
    }
  }

  &:nth-of-type(2) {
    top: -2.5rem;
    left: 69%;
    transition:
      opacity 0.5s 1s,
      top 0.5s 1s;

    .visible & {
      top: -2rem;
    }

    @media ${mqLargeMin} {
      top: -0.5rem;

      .visible & {
        top: 0.5rem;
      }
    }
  }
`;

type ProductBundleProps = {
  // The Shopify product fields this reads.
  productInfo: { title?: string; upsellProduct?: { value?: string } };
  bundleModule: BundleModule;
  product: Pick<Product, 'productSku'>;
};

export default function ProductBundle({
  productInfo,
  bundleModule,
  product,
}: ProductBundleProps) {
  const { getProductByGID } = useContext(ProductsContext);
  const upsellItem = useMemo(() => {
    if (!productInfo?.upsellProduct?.value) return null;
    return getProductByGID(productInfo.upsellProduct.value);
  }, [productInfo]);

  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false,
  });

  const bundleMedia = bundleModule.image;
  const currentMediaImage = useProgressiveImg({
    mediaItem: bundleMedia ?? {},
    mediaSize: 'large',
  });

  const { openCart } = useContext(CartContext);

  const handleButtonClicked = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    trackButtonEvent('Product Bundle CTA', window.location.pathname, {});
    openCart({ skus: [product.productSku, upsellItem.productSku] });
  };

  if (!upsellItem) return null;

  return (
    <BundleWrapper ref={ref} className={inView ? 'visible' : ''}>
      <BundleContent>
        <BundleImageWrapper>
          <BundleLabel>{bundleModule.imageLabels?.[0]}</BundleLabel>
          <BundleLabel>{bundleModule.imageLabels?.[1]}</BundleLabel>
          <img
            src={currentMediaImage.src}
            ref={currentMediaImage.ref}
            style={currentMediaImage.blurStyles}
            alt={bundleMedia?.alt}
          />
        </BundleImageWrapper>
        <BundleTextWrapper>
          <BundleContentTitle>{bundleModule.title}</BundleContentTitle>
          {/* ':' and ' ' are separate text nodes; SSR splits them with a comment. */}
          <p>
            <b>{productInfo.title}</b>
            {':'} {bundleModule.productText}
          </p>
          <p>
            <b>{upsellItem.name}</b>
            {':'} {bundleModule.upsellProductText}
          </p>
          <AddBundleToCart
            href={`/cart?cart=${product.productSku},${upsellItem.productSku}`}
            onClick={handleButtonClicked}
          >
            Add Bundle - Save 25%
          </AddBundleToCart>
        </BundleTextWrapper>
      </BundleContent>
    </BundleWrapper>
  );
}
